import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Trophy, Zap, MessageCircle, MapPin, Users, Sparkles, Gamepad2, ChevronDown } from 'lucide-react';
import { homeFeatures, heroImages, stats, faq } from '../siteContent';

const iconMap = { Trophy, Zap, MessageCircle, MapPin, Users, Sparkles, Gamepad2 };

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setVisible(true),
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, className = '' }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function Home() {
  const [openFaq, setOpenFaq] = useState(0);
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImages.hero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/85 via-neutral-950/75 to-neutral-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(250,204,21,0.12),transparent_60%)]" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 min-h-[88vh] flex flex-col items-center justify-center text-center py-24">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-yellow-400/30 bg-yellow-400/5 text-yellow-300 text-xs font-medium tracking-wider uppercase mb-8 backdrop-blur-sm">
            <Sparkles size={14} />
            Nouvelle expérience sociale mobile
          </div>
          <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-yellow-400 leading-[1.05]">
            Relevez des défis et<br />grimpez au classement !
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-neutral-300 leading-relaxed">
            BuzzBump combine défis urbains interactifs, messagerie dynamique et carte des scores pour des expériences sociales et ludiques uniques sur mobile.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center gap-4">
            <Link
              to="/notre-projet"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-yellow-400 text-neutral-900 font-semibold hover:bg-yellow-300 transition-colors shadow-lg shadow-yellow-400/20"
            >
              Découvrir le projet
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md border border-white/20 text-white hover:border-yellow-400 hover:text-yellow-400 transition-colors"
            >
              Nous contacter
            </Link>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-yellow-400/70 animate-bounce">
            <ChevronDown size={28} />
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-white/5 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          {stats.map((s, i) => {
            const Icon = iconMap[s.icon] || Sparkles;
            return (
              <Reveal key={s.label} delay={i * 80} className="flex items-center gap-4">
                <div className="h-12 w-12 grid place-items-center rounded-md bg-yellow-400/10 border border-yellow-400/20 text-yellow-400">
                  <Icon size={22} />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{s.value}</div>
                  <div className="text-sm text-neutral-400">{s.label}</div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* FEATURES alternating */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 space-y-28">
        {homeFeatures.map((f, i) => {
          const Icon = iconMap[f.icon] || Sparkles;
          const reverse = i % 2 === 1;
          return (
            <div key={f.id} className={`grid md:grid-cols-2 gap-12 items-center ${reverse ? 'md:[&>div:first-child]:order-2' : ''}`}>
              <Reveal>
                <div className="relative group">
                  <div className="absolute -inset-4 rounded-2xl bg-yellow-400/10 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative overflow-hidden rounded-2xl border border-white/10">
                    <img
                      src={f.image}
                      alt={f.title}
                      loading="lazy"
                      className="w-full h-[420px] object-cover group-hover:scale-105 transition-transform duration-[1200ms]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-neutral-950/60 via-transparent to-transparent" />
                  </div>
                </div>
              </Reveal>
              <Reveal delay={150}>
                <div>
                  <div className="inline-flex items-center gap-2 mb-5 px-3 py-1 rounded-full bg-yellow-400/10 text-yellow-400 text-xs font-semibold uppercase tracking-wider">
                    <Icon size={14} />
                    Feature {String(i + 1).padStart(2, '0')}
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-5">
                    {f.title}
                  </h3>
                  <p className="text-neutral-400 text-lg leading-relaxed mb-7">
                    {f.description}
                  </p>
                  <Link
                    to={f.link}
                    className="inline-flex items-center gap-2 text-yellow-400 font-semibold hover:gap-3 transition-all"
                  >
                    En savoir plus <ArrowRight size={18} />
                  </Link>
                </div>
              </Reveal>
            </div>
          );
        })}
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-24">
        <Reveal className="text-center mb-12">
          <div className="text-yellow-400 text-sm font-semibold tracking-wider uppercase mb-3">FAQ</div>
          <h2 className="text-4xl font-bold text-white">Questions fréquentes</h2>
        </Reveal>
        <div className="space-y-3">
          {faq.map((item, i) => (
            <div key={item.q} className="rounded-xl border border-white/10 bg-neutral-900/40 overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-white/[0.02] transition-colors"
              >
                <span className="font-semibold text-white">{item.q}</span>
                <ChevronDown
                  size={20}
                  className={`text-yellow-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}
                />
              </button>
              <div
                className={`grid transition-all duration-300 ${
                  openFaq === i ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                }`}
              >
                <div className="overflow-hidden">
                  <p className="px-6 pb-5 text-neutral-400 leading-relaxed">{item.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="relative overflow-hidden rounded-3xl border border-yellow-400/20 bg-gradient-to-br from-neutral-900 to-neutral-950 p-10 md:p-16 text-center">
          <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-yellow-400/20 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-yellow-400/10 blur-3xl" />
          <div className="relative">
            <h2 className="text-3xl md:text-5xl font-extrabold text-white max-w-3xl mx-auto leading-tight">
              Qu'attendez-vous pour rejoindre <span className="text-yellow-400">la ruche</span> ?
            </h2>
            <p className="mt-5 text-neutral-400 max-w-xl mx-auto">
              Inscrivez-vous à la bêta et soyez parmi les premiers à vivre l'expérience BuzzBump.
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-yellow-400 text-neutral-900 font-semibold hover:bg-yellow-300 transition-colors"
            >
              Nous contacter <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
